import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { SignupStateService } from '../../services/signup-state.service';
import { KycApiService, OcrRegistroData } from 'src/app/auth/services/kyc-api.service';
import {
  PersonRegistrationService,
  CheckDocumentDto,
  CheckDocumentResponse,
} from 'src/app/auth/services/person-registration.service';
import { GeolocationService } from '../../services/geolocation.service';
import {
  PASOS_COMUNES,
  PASOS_EMPRESA,
  toE164,
} from '../../constants/signup.constants';
import { erutEsDeOtraPersona } from '../../utils/erut-representante';
import { SelectedDocumentType } from '../../interfaces/signup.interfaces';
import { mapOcrResult } from '../../utils/map-ocr-result';
import { motivoEdadEnOcr } from '../../../../../shared/utils/edad-minima.util';

/**
 * Configuracion optimizada para captura de documentos
 * Valores basados en requisitos del backend KYC:
 * - AWS Rekognition: max 5MB
 * - Textract: min 50x50px, ideal > 1000px ancho
 */
const CAPTURE_CONFIG = {
  // Dimensiones optimas para OCR
  targetWidth: 1920,        // Ancho objetivo para buena calidad OCR
  minWidth: 1280,           // Minimo aceptable
  maxWidth: 2400,           // Maximo antes de comprimir

  // Compresion JPEG
  quality: 0.85,            // Calidad inicial (85%)
  minQuality: 0.5,          // Calidad minima aceptable
  qualityStep: 0.05,        // Reduccion por paso

  // Limites de archivo
  maxFileSizeMB: 4.5,       // Maximo para backend (AWS limit 5MB)
  targetFileSizeMB: 2,      // Objetivo para upload rapido

  format: 'image/jpeg' as const,

  // Aspect ratio de documento (cedula)
  documentAspectRatio: 1.586,  // Ancho / Alto de cedula
};

/**
 * Configuracion de camara por tipo de dispositivo
 */
const CAMERA_CONFIGS = {
  // Dispositivos de alta gama
  highEnd: {
    width: { ideal: 1920, min: 1280 },
    height: { ideal: 1080, min: 720 },
  },
  // Dispositivos de gama media
  midRange: {
    width: { ideal: 1280, min: 640 },
    height: { ideal: 720, min: 480 },
  },
  // Dispositivos de gama baja (fallback)
  lowEnd: {
    width: { ideal: 640 },
    height: { ideal: 480 },
  },
};

// Diccionario de errores del backend
const ERROR_MESSAGES: Record<string, string> = {
  // Archivo
  FILE_VALIDATION_ERROR: 'Formato de archivo no valido. Usa JPG o PNG.',
  FILE_SIZE_EXCEEDED: 'El archivo es muy grande. Maximo 10MB.',
  INVALID_FILE_TYPE: 'Tipo de archivo no permitido. Usa JPG o PNG.',
  FILE_REQUIRED: 'Debes capturar una imagen del documento.',

  // Documento
  DOCUMENT_IS_PHOTOCOPY: 'Debes tomar una foto del documento fisico, no una fotocopia. Asegurate de tener buena iluminacion.',
  DOCUMENT_SCREEN_RECAPTURE:
    'Debes tomar una foto del documento fisico, no de una pantalla. No fotografies el documento desde otro celular o monitor.',
  INVALID_DOCUMENT_COUNTRY: 'Este documento no corresponde al pais esperado.',
  INVALID_DOCUMENT_TYPE: 'El documento debe ser una cedula de identidad vigente.',
  DOCUMENT_NOT_RECOGNIZED: 'No pudimos leer el documento. Asegurate de que este completo y visible.',
  DOCUMENT_QUALITY_TOO_LOW: 'La imagen no es clara. Toma otra foto con mejor iluminacion.',
  DOCUMENT_CENSORED: 'El numero de identificacion esta tachado o censurado. Sube un documento donde sea claramente visible.',
  OCR_EXTRACTION_FAILED: 'No pudimos leer algunos datos. Toma otra foto mas nitida.',

  // AWS
  AWS_SERVICE_ERROR: 'Error del servidor. Intenta nuevamente.',
  AWS_CREDENTIALS_ERROR: 'Error de configuracion del servidor. Contacta a soporte.',
  AWS_TEXTRACT_ERROR: 'Error procesando el documento. Intenta nuevamente.',
  AWS_REKOGNITION_ERROR: 'Error analizando la imagen. Intenta nuevamente.',
  AWS_THROTTLING_ERROR: 'Demasiadas solicitudes. Espera un momento.',
  AWS_TIMEOUT_ERROR: 'El servidor tardo mucho. Intenta nuevamente.',
};

// Sub-fases del paso 5
type OcrSubStep = 'select-document' | 'capture-front' | 'capture-back';

@Component({
  selector: 'app-ocr-document-step',
  templateUrl: './ocr-document-step.component.html',
  styleUrls: ['./ocr-document-step.component.sass'],
})
export class OcrDocumentStepComponent implements OnInit, OnDestroy {
  @Output() onError = new EventEmitter<string>();
  @Output() onSuccess = new EventEmitter<string>();

  @ViewChild('video') videoElement!: ElementRef<HTMLVideoElement>;

  // Estado
  loading: boolean = false;
  showCameraModal: boolean = false;
  isReadyToCapture: boolean = false;
  currentImage: string = '';
  rotationAngle: number = 0;

  // Sub-paso actual
  currentSubStep: OcrSubStep = 'select-document';

  // Camera
  private alignmentInterval: any;
  private stream: MediaStream | null = null;

  constructor(
    public signupState: SignupStateService,
    private kycService: KycApiService,
    private personRegistrationService: PersonRegistrationService,
    private geolocationService: GeolocationService
  ) {}

  ngOnInit(): void {
    // Verificar que los pasos anteriores están completos
    if (!this.signupState.currentState.emailVerified) {
      this.signupState.goToStep(PASOS_COMUNES.OTP_EMAIL);
      return;
    }
    if (!this.signupState.currentState.phoneVerified) {
      this.signupState.goToStep(PASOS_COMUNES.OTP_WHATSAPP);
      return;
    }

    this.initializeSubStep();
  }

  ngOnDestroy(): void {
    this.cleanupCamera();
  }

  // ==================== INICIALIZACION ====================

  private initializeSubStep(): void {
    // Si es empresa, saltar seleccion (siempre Chile)
    if (this.signupState.isEmpresa) {
      this.signupState.setSelectedDocumentType('chile');
      this.currentSubStep = this.signupState.documentCapturePhase === 'back'
        ? 'capture-back'
        : 'capture-front';
      return;
    }

    // Personal: verificar si ya selecciono tipo
    if (this.signupState.hasSelectedDocumentType) {
      // Ya selecciono, ir a captura
      this.currentSubStep = this.signupState.documentCapturePhase === 'back'
        ? 'capture-back'
        : 'capture-front';

      // Cargar imagen existente si hay
      if (this.signupState.documentCapturePhase === 'front' && this.signupState.currentState.documentImageFront) {
        this.currentImage = this.signupState.currentState.documentImageFront;
      } else if (this.signupState.documentCapturePhase === 'back' && this.signupState.currentState.documentImageBack) {
        this.currentImage = this.signupState.currentState.documentImageBack;
      }
    } else {
      // Mostrar seleccion de tipo de documento
      this.currentSubStep = 'select-document';
    }
  }

  // ==================== SELECCION DE DOCUMENTO ====================

  selectDocumentType(type: SelectedDocumentType): void {
    this.signupState.setSelectedDocumentType(type);
    this.signupState.setDocumentCapturePhase('front');
    this.currentSubStep = 'capture-front';
  }

  // ==================== CAMARA ====================

  async openCameraModal(event?: Event): Promise<void> {
    if (event) event.preventDefault();
    this.loading = true;
    this.showCameraModal = true;
    this.isReadyToCapture = false;

    // Esperar a que el DOM se actualice
    setTimeout(async () => {
      if (this.videoElement) {
        await this.initCamera();
      }
      this.loading = false;
    }, 300);
  }

  closeCameraModal(): void {
    this.showCameraModal = false;
    this.cleanupCamera();
  }

  private cleanupCamera(): void {
    // Detener todos los tracks del stream
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.stop();
      });
      this.stream = null;
    }

    // Limpiar el video element
    const video = this.videoElement?.nativeElement;
    if (video) {
      video.srcObject = null;
      video.load(); // Forzar liberacion de recursos
    }

    // Limpiar intervalo de alineacion
    if (this.alignmentInterval) {
      clearInterval(this.alignmentInterval);
      this.alignmentInterval = null;
    }
  }

  /**
   * Inicializa la camara con fallback para diferentes dispositivos
   */
  private async initCamera(): Promise<void> {
    const video = this.videoElement.nativeElement as HTMLVideoElement;

    // Intentar con diferentes configuraciones (alta -> media -> baja gama)
    const configAttempts = ['highEnd', 'midRange', 'lowEnd'] as const;

    for (const configType of configAttempts) {
      try {
        const constraints = await this.buildCameraConstraints(configType);
        this.stream = await navigator.mediaDevices.getUserMedia(constraints);

        video.srcObject = this.stream;
        video.setAttribute('playsinline', 'true'); // Importante para iOS
        video.setAttribute('autoplay', 'true');
        video.muted = true; // Necesario para autoplay en algunos navegadores

        await video.play();

        // Aplicar ajustes avanzados de camara
        await this.applyOptimalCameraSettings(this.stream);

        this.isReadyToCapture = true;
        return; // Exito, salir del loop

      } catch (err) {
        // Continuar con la siguiente configuracion
      }
    }

    // Si ninguna configuracion funciono
    this.onError.emit('No se pudo acceder a la camara. Verifica los permisos.');
    this.closeCameraModal();
  }

  /**
   * Construye constraints de camara segun el tipo de dispositivo
   */
  private async buildCameraConstraints(
    configType: 'highEnd' | 'midRange' | 'lowEnd'
  ): Promise<MediaStreamConstraints> {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isMobile = isIOS || isAndroid;

    // iOS: solicitar permisos primero para poder enumerar dispositivos
    if (isIOS && configType === 'highEnd') {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
        tempStream.getTracks().forEach(t => t.stop());
      } catch (e) {
        // Ignorar, continuamos con el intento
      }
    }

    const config = CAMERA_CONFIGS[configType];

    // Base constraints
    const videoConstraints: MediaTrackConstraints = {
      ...config,
      facingMode: isMobile ? 'environment' : 'user', // Trasera en movil, frontal en PC
    };

    // Intentar obtener dispositivo especifico en movil
    if (isMobile && configType === 'highEnd') {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(d => d.kind === 'videoinput');
        const backCamera = cameras.find(c => /back|rear|environment|0/i.test(c.label));

        if (backCamera?.deviceId) {
          videoConstraints.deviceId = { exact: backCamera.deviceId };
        }
      } catch (e) {
        // Usar facingMode como fallback
      }
    }

    return {
      video: videoConstraints,
      audio: false,
    };
  }

  /**
   * Aplica configuraciones optimas de camara para captura de documentos
   */
  private async applyOptimalCameraSettings(stream: MediaStream): Promise<void> {
    try {
      const [track] = stream.getVideoTracks();
      const capabilities = track.getCapabilities() as any;
      const settings: MediaTrackConstraintSet[] = [];

      // 1. ENFOQUE: Continuo para documentos
      if (capabilities.focusMode) {
        if (capabilities.focusMode.includes('continuous')) {
          settings.push({ focusMode: 'continuous' } as any);
        } else if (capabilities.focusMode.includes('auto')) {
          settings.push({ focusMode: 'auto' } as any);
        }
      }

      // 2. EXPOSICION: Ligeramente aumentada para documentos
      if (capabilities.exposureMode) {
        if (capabilities.exposureMode.includes('continuous')) {
          settings.push({ exposureMode: 'continuous' } as any);
        }
      }

      if (capabilities.exposureCompensation) {
        const { min, max } = capabilities.exposureCompensation;
        // Aumentar ligeramente la exposicion para mejor lectura
        const targetExposure = Math.min(0.5, max);
        if (targetExposure >= min) {
          settings.push({ exposureCompensation: targetExposure } as any);
        }
      }

      // 3. BALANCE DE BLANCOS: Automatico
      if (capabilities.whiteBalanceMode?.includes('continuous')) {
        settings.push({ whiteBalanceMode: 'continuous' } as any);
      }

      // 4. ISO: Automatico si esta disponible
      if (capabilities.iso && capabilities.iso.min && capabilities.iso.max) {
        // Dejar en automatico (no establecer valor fijo)
      }

      // 5. ZOOM: Reset a 1x
      if (capabilities.zoom && capabilities.zoom.min) {
        settings.push({ zoom: capabilities.zoom.min } as any);
      }

      // Aplicar todas las configuraciones
      if (settings.length > 0) {
        await track.applyConstraints({ advanced: settings });
      }

    } catch (err) {
      // No es critico si falla, la camara funcionara con valores por defecto
    }
  }

  // ==================== CAPTURA ====================

  /**
   * Captura el documento desde el video
   * - Recorta al area del marco (solo el documento)
   * - Optimiza para OCR
   * - Comprime para cumplir limites del backend
   */
  captureDocument(): void {
    const video = this.videoElement.nativeElement as HTMLVideoElement;

    if (!video || video.videoWidth === 0) {
      this.onError.emit('La camara no esta lista. Intenta de nuevo.');
      return;
    }

    this.loading = true;

    try {
      // Capturar y procesar imagen
      const base64 = this.captureDocumentArea(video);
      this.currentImage = base64;
      this.rotationAngle = 0;
      this.closeCameraModal();

    } catch (err) {
      this.onError.emit('Error al capturar imagen. Intenta de nuevo.');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Captura solo el area del documento (dentro del marco)
   * Esto reduce el tamaño y mejora el OCR al enviar solo lo relevante
   */
  private captureDocumentArea(video: HTMLVideoElement): string {
    const { videoWidth, videoHeight } = video;

    // Calcular el area del marco (85% del ancho, aspect ratio de documento)
    const frameWidthPercent = 0.85;
    const frameWidth = Math.round(videoWidth * frameWidthPercent);
    const frameHeight = Math.round(frameWidth / CAPTURE_CONFIG.documentAspectRatio);

    // Centrar el recorte
    const startX = Math.round((videoWidth - frameWidth) / 2);
    const startY = Math.round((videoHeight - frameHeight) / 2);

    // Crear canvas para el recorte
    const canvas = document.createElement('canvas');

    // Calcular dimensiones finales (respetar maxWidth)
    let targetWidth = frameWidth;
    let targetHeight = frameHeight;

    if (targetWidth > CAPTURE_CONFIG.targetWidth) {
      const scale = CAPTURE_CONFIG.targetWidth / targetWidth;
      targetWidth = CAPTURE_CONFIG.targetWidth;
      targetHeight = Math.round(frameHeight * scale);
    }

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No se pudo crear contexto de canvas');

    // Configurar calidad de renderizado
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Dibujar solo el area del documento
    ctx.drawImage(
      video,
      startX, startY, frameWidth, frameHeight,  // Origen (recorte)
      0, 0, targetWidth, targetHeight            // Destino (canvas)
    );

    // Comprimir progresivamente hasta cumplir el limite
    return this.compressToTargetSize(canvas);
  }

  /**
   * Comprime la imagen progresivamente hasta cumplir el limite de tamaño
   */
  private compressToTargetSize(canvas: HTMLCanvasElement): string {
    let quality = CAPTURE_CONFIG.quality;
    let result = canvas.toDataURL(CAPTURE_CONFIG.format, quality);

    // Reducir calidad hasta alcanzar el tamaño objetivo
    while (
      this.getFileSizeMB(result) > CAPTURE_CONFIG.maxFileSizeMB &&
      quality > CAPTURE_CONFIG.minQuality
    ) {
      quality -= CAPTURE_CONFIG.qualityStep;
      result = canvas.toDataURL(CAPTURE_CONFIG.format, quality);
    }

    // Si aun es muy grande, reducir dimensiones
    if (this.getFileSizeMB(result) > CAPTURE_CONFIG.maxFileSizeMB) {
      return this.reduceImageSize(canvas, quality);
    }

    return result;
  }

  /**
   * Reduce las dimensiones de la imagen si la compresion no es suficiente
   */
  private reduceImageSize(originalCanvas: HTMLCanvasElement, quality: number): string {
    const scale = 0.75; // Reducir al 75%
    const newWidth = Math.round(originalCanvas.width * scale);
    const newHeight = Math.round(originalCanvas.height * scale);

    const smallCanvas = document.createElement('canvas');
    smallCanvas.width = newWidth;
    smallCanvas.height = newHeight;

    const ctx = smallCanvas.getContext('2d');
    if (!ctx) return originalCanvas.toDataURL(CAPTURE_CONFIG.format, quality);

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(originalCanvas, 0, 0, newWidth, newHeight);

    return smallCanvas.toDataURL(CAPTURE_CONFIG.format, quality);
  }

  /**
   * Calcula el tamaño en MB de una imagen base64
   */
  private getFileSizeMB(base64: string): number {
    const base64Data = base64.split(',')[1] || '';
    // Base64 usa ~4 bytes por cada 3 bytes originales
    return (base64Data.length * 0.75) / (1024 * 1024);
  }

  // ==================== ROTACION ====================

  rotateLeft(): void {
    this.rotationAngle = (this.rotationAngle - 90 + 360) % 360;
  }

  rotateRight(): void {
    this.rotationAngle = (this.rotationAngle + 90) % 360;
  }

  retakeImage(event?: Event): void {
    if (event) event.preventDefault();
    this.currentImage = '';
    this.rotationAngle = 0;
    this.openCameraModal();
  }

  /**
   * Aplica la rotacion a la imagen y la recomprime
   * Retorna imagen lista para enviar al backend
   */
  private async applyRotationAndOptimize(base64: string, angle: number): Promise<string> {
    // Si no hay rotacion, solo recomprimir si es necesario
    if (angle === 0) {
      return this.ensureOptimalSize(base64);
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('No se pudo crear contexto'));
          return;
        }

        // Ajustar dimensiones segun rotacion
        if (angle % 180 === 0) {
          canvas.width = img.width;
          canvas.height = img.height;
        } else {
          canvas.width = img.height;
          canvas.height = img.width;
        }

        // Configurar calidad
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Aplicar rotacion
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((angle * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        // Comprimir resultado
        const result = this.compressToTargetSize(canvas);
        resolve(result);
      };

      img.onerror = () => reject(new Error('Error al cargar imagen'));
      img.src = base64;
    });
  }

  /**
   * Asegura que la imagen cumpla con los requisitos de tamaño
   */
  private ensureOptimalSize(base64: string): string {
    const sizeMB = this.getFileSizeMB(base64);

    if (sizeMB <= CAPTURE_CONFIG.maxFileSizeMB) {
      return base64;
    }

    // Necesita recompresion
    return new Promise<string>((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(this.compressToTargetSize(canvas));
        } else {
          resolve(base64);
        }
      };
      img.onerror = () => resolve(base64);
      img.src = base64;
    }) as unknown as string;
  }

  // ==================== CONFIRMAR DOCUMENTO ====================

  async confirmDocument(): Promise<void> {
    if (!this.currentImage) return;

    this.loading = true;

    try {
      // Aplicar rotacion y optimizar imagen para backend
      const finalImage = await this.applyRotationAndOptimize(
        this.currentImage,
        this.rotationAngle
      );

      if (this.currentSubStep === 'capture-front') {
        await this.processFrontCapture(finalImage);
      } else if (this.currentSubStep === 'capture-back') {
        await this.processBackCapture(finalImage);
      }
    } catch (err: any) {
      const msg = this.handleOcrError(err);
      this.onError.emit(msg);
      this.currentImage = '';
    } finally {
      this.loading = false;
    }
  }

  private async processFrontCapture(image: string): Promise<void> {
    // Guardar imagen frontal
    this.signupState.setDocumentImageFront(image);

    // Enviar a OCR
    const file = this.base64ToFile(image, 'document-front.jpg');
    const state = this.signupState.currentState;
    const registro: OcrRegistroData = {
      cognitoId: state.cognitoId,
      tipo: this.signupState.isEmpresa ? 'EMPRESA' : 'PERSONA',
      email: state.formData?.email || undefined,
      whatsapp: state.formData?.phoneCode && state.formData?.phone
        ? toE164(state.formData.phoneCode, state.formData.phone)
        : undefined,
      ip: this.geolocationService.cachedGeoData?.ip || undefined,
    };
    let ocrResult: any;

    if (this.signupState.documentType === 'chile') {
      ocrResult = await this.kycService.ocrChile(file, registro);
    } else {
      ocrResult = await this.kycService.ocrVenezuela(file, registro);
    }

    // Validar respuesta OCR
    this.validateOcrResponse(ocrResult);

    // Mapear datos OCR
    const ocrData = mapOcrResult(ocrResult, this.signupState.documentType);

    // Verificar si documento ya esta registrado (ANTES de guardar)
    await this.checkDocumentRegistration(ocrData);

    // Guardar datos OCR (solo si paso la verificacion)
    this.signupState.setOcrData(ocrData);

    // Y los campos crudos, que son los que la consolidacion de empresa espera
    // en `data4.ocr.campos`. Van aparte de `ocrData` a proposito: ver
    // `ocrCampos` en signup.interfaces.ts.
    this.signupState.setOcrCampos(ocrResult?.campos);

    // Empresa: el e-RUT ya se cargo en el paso 5, asi que el cruce contra
    // `rutUsuario` se puede hacer ACA y no dos pantallas mas adelante. Si el
    // e-RUT lo descargo otra persona, capturar el reverso y la selfie es trabajo
    // tirado: la pantalla del representante legal va a bloquear igual. Se salta
    // directo ahi, que es la unica que sabe explicar el desencuentro y ofrecer
    // cargar otro e-RUT.
    if (this.signupState.isEmpresa && this.erutEsDeOtroRepresentante(ocrData)) {
      this.onError.emit(
        'El e-RUT que cargaste lo descargo otra persona. Necesitamos el que ' +
          'descarga el mismo representante legal cuyo documento acabas de leer.'
      );
      await this.signupState.goToStep(PASOS_EMPRESA.REPRESENTANTE);
      return;
    }

    this.onSuccess.emit('Documento frontal procesado correctamente');

    // Verificar si necesita captura trasera
    if (this.signupState.documentType === 'chile') {
      // Chile: continuar con captura trasera
      this.signupState.setDocumentCapturePhase('back');
      this.currentSubStep = 'capture-back';
      this.currentImage = '';
      this.rotationAngle = 0;
    } else {
      // Venezuela: paso completado, avanzar
      await this.signupState.nextStep();
    }
  }

  /** ¿El e-RUT cargado lo descargo alguien distinto del titular de este documento? */
  private erutEsDeOtroRepresentante(ocrData: any): boolean {
    return erutEsDeOtraPersona(
      ocrData?.documentNumberClean || ocrData?.documentNumber,
      this.signupState.erutAnalysis?.rutUsuario
    );
  }

  /**
   * Verifica si el documento ya esta registrado en el sistema
   * Si esta registrado, muestra error, espera 10 segundos y redirige al paso de registro
   * Se ejecuta despues del OCR frontal, antes de continuar con foto trasera (Chile) o paso 6 (Venezuela)
   */
  private async checkDocumentRegistration(ocrData: any): Promise<void> {
    const isChile = this.signupState.documentType === 'chile';

    // Determinar tipo de documento para Chile (RUN = extranjero, RUT = chileno)
    let documentType: 'RUT' | 'RUN' | 'CCVE';
    if (isChile) {
      // CEDULA_IDENTIDAD_EXTRANJERO = RUN, CEDULA_IDENTIDAD = RUT
      documentType = ocrData.documentType?.includes('EXTRANJERO') ? 'RUN' : 'RUT';
    } else {
      documentType = 'CCVE';
    }

    const checkData: CheckDocumentDto = {
      country: isChile ? 'CL' : 'VE',
      documentNumber: ocrData.documentNumberClean, // runSinFormato o numeroCedulaSinFormato
      documentType: documentType,
    };

    let result: CheckDocumentResponse;

    try {
      result = await this.personRegistrationService.checkDocument(checkData);
    } catch (error: any) {
      // Errores de red o servidor: no bloquear el registro por una falla del check
      return;
    }

    if (!result.isRegistered) {
      return;
    }

    if (this.signupState.isEmpresa) {
      // Empresa: el documento validado es el del representante legal, no el de la
      // empresa. Ser representante legal es un rol ortogonal a ser cliente persona
      // natural o pagador, así que no se bloquea acá: el RUT de la empresa lo
      // revisan el paso del e-RUT y la consolidación. (Antes se miraba
      // `registeredAs`, que el back ya no devuelve: R5.)
      return;
    }

    // Persona: cualquier registro previo bloquea el alta. El mensaje no dice de
    // quién es el documento (R5).
    const errorMessage =
      'Este documento ya está registrado en la plataforma. Redirigiendo en 10 segundos...';

    // Emitir error para mostrar en pantalla
    this.onError.emit(errorMessage);

    // Esperar 10 segundos antes de redirigir
    await this.delay(10000);

    // Resetear todo el estado y volver al paso 1 (seleccion tipo de cuenta)
    this.signupState.reset();
    await this.signupState.goToStep(PASOS_COMUNES.TIPO_CUENTA);

    // Lanzar error para detener el flujo
    throw new Error('Documento ya registrado');
  }

  /**
   * Utilidad para esperar un tiempo determinado
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async processBackCapture(image: string): Promise<void> {
    // Solo guardar imagen trasera (NO enviar a OCR)
    this.signupState.setDocumentImageBack(image);

    this.onSuccess.emit('Documento trasero capturado correctamente');

    // Paso completado, avanzar
    await this.signupState.nextStep();
  }

  private validateOcrResponse(response: any): void {
    // Verificar sanciones
    if (response.verificacionSanciones?.hasMatches) {
      const riskLevel = response.verificacionSanciones.riskLevel;
      if (riskLevel === 'CRITICAL' || riskLevel === 'HIGH') {
        throw new Error('No es posible continuar con el registro. Contacte a soporte.');
      }
    }

    // La edad va antes que camposCompletos: el KYC también lo deja en false para
    // un menor, y el mensaje de "Faltan datos" no le explicaba nada al usuario.
    const motivoEdad = motivoEdadEnOcr(response, this.signupState.isEmpresa);
    if (motivoEdad) {
      throw new Error(motivoEdad);
    }

    const validacion = response.validacion;

    if (validacion?.estaVencido) {
      throw new Error('El documento esta vencido. Debes renovarlo para continuar.');
    }

    if ('runValido' in validacion && !validacion.runValido) {
      throw new Error('El RUN del documento no es valido.');
    }

    if ('cedulaValida' in validacion && !validacion.cedulaValida) {
      throw new Error('El numero de cedula no es valido.');
    }

    if (validacion?.confianzaOCR < 70) {
      throw new Error('La imagen no es clara. Toma otra foto con mejor iluminacion.');
    }

    if (validacion && !validacion.camposCompletos) {
      const advertencias = validacion.advertencias?.join(', ') || 'algunos campos';
      throw new Error(`Faltan datos: ${advertencias}. Captura el documento completo.`);
    }

    if ('fechasCoherentes' in validacion && !validacion.fechasCoherentes) {
      throw new Error('Las fechas del documento no son coherentes. Verifica el documento.');
    }
  }

  private handleOcrError(error: any): string {
    const errorCode = error?.error?.error?.code ||
                      error?.error?.errorCode ||
                      error?.code ||
                      'UNKNOWN';

    if (ERROR_MESSAGES[errorCode]) {
      return ERROR_MESSAGES[errorCode];
    }

    const backendMessage = error?.error?.error?.message ||
                           error?.error?.message ||
                           error?.message;

    if (backendMessage) {
      return backendMessage;
    }

    return 'Error procesando el documento. Intenta nuevamente.';
  }

  private base64ToFile(base64: string, filename: string): File {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  // ==================== NAVEGACION ====================

  async goBack(): Promise<void> {
    if (this.currentSubStep === 'capture-back') {
      // Volver a captura frontal
      this.signupState.setDocumentCapturePhase('front');
      this.currentSubStep = 'capture-front';
      this.currentImage = this.signupState.currentState.documentImageFront || '';
      this.rotationAngle = 0;
    } else if (this.currentSubStep === 'capture-front' && !this.signupState.isEmpresa) {
      // Personal: volver a seleccion de documento
      this.signupState.setSelectedDocumentType(null);
      this.signupState.resetDocumentImages();
      this.currentSubStep = 'select-document';
      this.currentImage = '';
    } else {
      // Volver al paso anterior
      await this.signupState.prevStep();
    }
  }

  // ==================== GETTERS ====================

  get documentTypeLabel(): string {
    const type = this.signupState.documentType;
    if (type === 'chile') return 'cedula chilena (RUT)';
    if (type === 'venezuela') return 'cedula venezolana';
    return 'documento de identidad';
  }

  get exampleImage(): string {
    const type = this.signupState.documentType;
    const phase = this.currentSubStep;

    if (type === 'chile') {
      return phase === 'capture-back'
        ? 'assets/img/reversoChile.jpeg'
        : 'assets/img/runChile.png';
    }
    if (type === 'venezuela') return 'assets/img/cedulaVenezuela.png';
    return 'assets/img/runChile.png';
  }

  get isBackCapture(): boolean {
    return this.currentSubStep === 'capture-back';
  }

  get captureTitle(): string {
    if (this.currentSubStep === 'capture-back') {
      return 'Captura el reverso de tu documento';
    }
    return `Captura tu ${this.documentTypeLabel}`;
  }

  get captureSubtext(): string {
    if (this.currentSubStep === 'capture-back') {
      return 'Alinea el reverso del documento como en el ejemplo y toma la foto.';
    }
    return 'Alinea el documento como en el ejemplo y toma la foto frontal.';
  }

  get phaseIndicator(): string {
    if (this.signupState.documentType !== 'chile') return '';
    return this.currentSubStep === 'capture-back' ? 'Paso 2 de 2' : 'Paso 1 de 2';
  }

  get showPhaseIndicator(): boolean {
    return this.signupState.documentType === 'chile' &&
           (this.currentSubStep === 'capture-front' || this.currentSubStep === 'capture-back');
  }
}
